// ─────────────────────────────────────────────────────────────────────────────
// analytics.js  —  Google Analytics 4 service
//
// All GA logic is centralised here so the rest of the app imports clean
// helper functions and never touches ReactGA directly.
//
// Environment variable (set in .env):
//   REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX
//
// GA only initialises and sends events in production builds.
// During local development (NODE_ENV !== 'production') every function is a
// no-op so dev traffic never pollutes your GA reports.
// ─────────────────────────────────────────────────────────────────────────────

import ReactGA from 'react-ga4';

// ── Config ───────────────────────────────────────────────────────────────────
// CRA exposes only REACT_APP_* env vars to the browser bundle at build time.
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Guard: only run in production AND when an ID is actually configured.
const IS_ENABLED =
  process.env.NODE_ENV === 'production' && Boolean(MEASUREMENT_ID);

// ── Initialization ────────────────────────────────────────────────────────────
/**
 * initGA()
 * Call once at app startup (e.g. inside the root component's useEffect).
 * react-ga4 is idempotent — calling it twice is safe but unnecessary.
 */
export const initGA = () => {
  if (!IS_ENABLED) return;
  ReactGA.initialize(MEASUREMENT_ID);
};

// ── Page Views ─────────────────────────────────────────────────────────────────
/**
 * trackPageView(path)
 * Sends a GA4 page_view hit.
 * Automatically called by <RouteTracker /> on every React Router navigation.
 *
 * @param {string} path  e.g. '/shop' or '/product/modern-clock?ref=home'
 */
export const trackPageView = (path) => {
  if (!IS_ENABLED) return;
  ReactGA.send({ hitType: 'pageview', page: path });
};

// ── Generic Event ─────────────────────────────────────────────────────────────
/**
 * trackEvent(eventName, params)
 * Low-level wrapper around ReactGA.event().
 * Prefer the typed helpers below for standard GA4 e-commerce events.
 *
 * @param {string} eventName  GA4 event name (snake_case recommended)
 * @param {object} params     Any additional GA4 event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!IS_ENABLED) return;
  ReactGA.event(eventName, params);
};

// ── E-Commerce Events ─────────────────────────────────────────────────────────

/**
 * trackViewItem(product)
 * GA4 recommended event: view_item
 * Fire when a user lands on a product detail page.
 *
 * @param {object} product  Product object from the API
 */
export const trackViewItem = (product) => {
  trackEvent('view_item', {
    currency: 'INR',
    value: parseFloat(product.price) || 0,
    items: [
      {
        item_id: String(product.id),
        item_name: product.name,
        item_category: product.category || '',
        price: parseFloat(product.price) || 0,
        quantity: 1,
      },
    ],
  });
};

/**
 * trackAddToCart(product, quantity, price)
 * GA4 recommended event: add_to_cart
 * Fire when a user successfully adds an item to the cart.
 *
 * @param {object} product   Product object (may include variant overrides)
 * @param {number} quantity  Number of units added
 * @param {number} price     Effective price (accounts for variants/discounts)
 */
export const trackAddToCart = (product, quantity = 1, price) => {
  const effectivePrice = parseFloat(price ?? product.price) || 0;
  trackEvent('add_to_cart', {
    currency: 'INR',
    value: effectivePrice * quantity,
    items: [
      {
        item_id: String(product.id),
        item_name: product.name,
        item_category: product.category || '',
        price: effectivePrice,
        quantity,
      },
    ],
  });
};

/**
 * trackBeginCheckout(cartItems, total)
 * GA4 recommended event: begin_checkout
 * Fire when a user clicks "Proceed to Checkout".
 *
 * @param {Array}  cartItems  Array of cart item objects
 * @param {number} total      Cart grand total in INR
 */
export const trackBeginCheckout = (cartItems, total) => {
  trackEvent('begin_checkout', {
    currency: 'INR',
    value: parseFloat(total) || 0,
    items: cartItems.map((item) => ({
      item_id: String(item.product?.id ?? item.id),
      item_name: item.product?.name ?? item.name,
      price: parseFloat(item.product?.price ?? item.price) || 0,
      quantity: item.quantity,
    })),
  });
};

/**
 * trackPurchase(order)
 * GA4 recommended event: purchase
 * Fire once on the Order Success page after the order is confirmed.
 *
 * @param {object} order  Full order object returned by the backend
 */
export const trackPurchase = (order) => {
  trackEvent('purchase', {
    transaction_id: String(order.order_id),
    currency: 'INR',
    value: parseFloat(order.total_amount) || 0,
    items: (order.items ?? []).map((item) => ({
      item_id: String(item.product_id ?? item.id),
      item_name: item.product_name,
      price: parseFloat(item.product_price) || 0,
      quantity: item.quantity,
    })),
  });
};

/**
 * trackLogin(method)
 * GA4 recommended event: login
 * Fire after a user successfully signs in.
 *
 * @param {string} method  Authentication method, e.g. 'email'
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', { method });
};

/**
 * trackSignUp(method)
 * GA4 recommended event: sign_up
 * Fire after a user successfully creates an account.
 *
 * @param {string} method  Registration method, e.g. 'email'
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', { method });
};

/**
 * trackSearch(searchTerm)
 * GA4 recommended event: search
 * Fire when a user submits a search query.
 *
 * @param {string} searchTerm  The text the user searched for
 */
export const trackSearch = (searchTerm) => {
  trackEvent('search', { search_term: searchTerm });
};
